import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Box } from '../../../../1-primative';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '../../../../2-component';
import { PdfViewer } from '../../../../2-component/pdfViewer/pdfViewer';
import { useGlobalToast } from '../../../../../providers/globalToastProvider';
import { translateBackend } from '../../../../../assets/i18n/config';
import { FETCH_FORM_COMPLETIONS } from './manageFormCompletions';

const FETCH_FORM_COMPLETION = gql`
  query fetchFormCompletion($formCompletionId: ObjectID!) {
    fetchFormCompletion(formCompletionId: $formCompletionId) {
      id
      translatedName { en fr }
      templateType
      unsignedUrl
      state
    }
  }
`;

const ARCHIVE_FORM_COMPLETION = gql`
  mutation archiveFormCompletion($input: ArchiveFormCompletionInput!) {
    archiveFormCompletion(input: $input) {
      formCompletion {
        id
        state
      }
    }
  }
`;

export const ViewFormDialog = ({
  formCompletionId,
  onClose,
  onArchive,
  canArchive,
}: {
  formCompletionId: string;
  onClose: () => void;
  onArchive?: () => void;
  canArchive: boolean;
}) => {
  const { t } = useTranslation(['workflowCompletions', 'shared']);
  const { showToast } = useGlobalToast();
  const [isArchiving, setIsArchiving] = useState(false);

  const { data, loading } = useQuery(FETCH_FORM_COMPLETION, {
    variables: { formCompletionId },
    fetchPolicy: 'no-cache',
  });

  const [archiveFormCompletion] = useMutation(ARCHIVE_FORM_COMPLETION);

  const handleArchive = async () => {
    try {
      setIsArchiving(true);
      await archiveFormCompletion({
        variables: {
          input: {
            formCompletionId,
          },
        },
        refetchQueries: [FETCH_FORM_COMPLETIONS],
      });
      showToast({ severity: 'success', message: t('workflowCompletions:formArchived') });
      onArchive?.();
      onClose();
    } catch (error) {
      showToast({ severity: 'error', message: t('workflowCompletions:formArchiveError') });
    } finally {
      setIsArchiving(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={true} onClose={onClose} fullWidth maxWidth='md'>
        <DialogContent>
          <Box display='flex' justifyContent='center' alignItems='center' minHeight={200}>
            <CircularProgress/>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  const formCompletion = data?.fetchFormCompletion;

  if (!formCompletion) {
    return null;
  }

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle onClose={onClose}>{translateBackend(formCompletion.translatedName)}</DialogTitle>
      <DialogContent>
        <Box display='flex' flexDirection='column' gap={2}>
          {formCompletion.templateType === 'PDF' && formCompletion.unsignedUrl && (
            <Box width='100%'>
              <PdfViewer pdfUrl={formCompletion.unsignedUrl} fullWidth/>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogFooter>
        <Box display='flex' justifyContent='space-between' width='100%'>
          {formCompletion.state !== 'ARCHIVED' && canArchive && (
            <Button
              label={t('shared:archive')}
              variant='tonal'
              color='destructive'
              onClick={handleArchive}
              disabled={isArchiving}
            />
          )}
          <Box display='flex' gap={2}>
            <Button
              label={t('shared:download')}
              variant='tonal'
              onClick={() => window.open(formCompletion.unsignedUrl, '_blank')}
            />
          </Box>
        </Box>
      </DialogFooter>
    </Dialog>
  );
};
